import { useState, useEffect, useCallback, useRef } from 'react';
/**
 * Custom Hook to manage adaptive scaling for PDF pages within a container.
 * It calculates an effective scale for each page to fit the container width,
 * combined with a user-defined zoom level.
 */
export function usePdfAdaptiveScale({ pdfDocument, containerRef, userScale, numPages, }) {
    const effectRunCountRef = useRef(0);
    const docIdForLog = pdfDocument?.fingerprints?.[0] || // Primary identifier
        (numPages ? `doc-pages:${numPages}` : 'no-doc-fingerprint-or-pages'); // Fallback identifier
    const [pageOriginalWidths, setPageOriginalWidths] = useState([]);
    const [containerWidth, setContainerWidth] = useState(0);
    // Effect for ResizeObserver to track container width changes
    useEffect(() => {
        effectRunCountRef.current += 1;
        const element = containerRef.current;
        if (!element) {
            return;
        }
        const observer = new ResizeObserver(entries => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });
        observer.observe(element);
        if (element.clientWidth !== containerWidth) {
            const initialWidth = element.clientWidth;
            setContainerWidth(initialWidth);
        }
        else {
            /* empty */
        }
        return () => {
            observer.disconnect();
        };
    }, [containerRef, docIdForLog]);
    // Effect for processing PDF document to get individual page widths
    useEffect(() => {
        if (!pdfDocument || !numPages) {
            setPageOriginalWidths([]);
            return;
        }
        let isMounted = true;
        const fetchPageWidths = async () => {
            const widths = [];
            try {
                for (let i = 1; i <= numPages; i++) {
                    const page = await pdfDocument.getPage(i);
                    const viewport = page.getViewport({ scale: 1.0 });
                    if (isMounted) {
                        widths.push({ page: i, width: viewport.width });
                    }
                    else {
                        break;
                    }
                }
                if (isMounted) {
                    setPageOriginalWidths(widths);
                }
            }
            catch (error) {
                if (isMounted) {
                    setPageOriginalWidths([]);
                }
            }
        };
        fetchPageWidths();
        return () => {
            isMounted = false;
        };
    }, [pdfDocument, numPages]);
    const getEffectiveScale = useCallback((pageNumber) => {
        const pageInfo = pageOriginalWidths.find(p => p.page === pageNumber);
        let calculatedScale;
        let reason = '';
        if (pageInfo && containerWidth > 0 && pageInfo.width > 0) {
            const baseFitToWidthScale = containerWidth / pageInfo.width;
            calculatedScale = baseFitToWidthScale * userScale;
            reason = 'Calculated with pageInfo and containerWidth';
        }
        else {
            calculatedScale = userScale;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            reason = 'Fallback to userScale';
        }
        return calculatedScale;
    }, [pageOriginalWidths, containerWidth, userScale]);
    return { getEffectiveScale, containerWidth };
}
//# sourceMappingURL=usePdfAdaptiveScale.js.map